import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { useQuery } from '@apollo/client'
import { useTranslations } from 'next-intl'
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  TooltipProps,
  Legend,
  AreaChart,
  Area,
} from 'recharts'
import { subYears } from 'date-fns'

import { getHashpriceHistory } from '@/api/hashprice'
import { IHashpriceHistory } from '@/types/hashprice.interface'
import { getMiningProfitByHashrate } from '@/utils/getMiningProfitByHashrate'

type GraphParameterLabelsProps = {
  hashrate: {
    label: string
    unit: string
    value: number
  }
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  currency: 'USD' | 'BTC'
}

const CustomTooltip = ({
  active,
  payload,
  label,
  currency,
}: CustomTooltipProps) => {
  const t = useTranslations()
  if (active && payload && payload.length) {
    return (
      <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        boxShadow="md"
        px={4}
        py={3}
        minW="160px"
        opacity={0.8}
      >
        <Text fontSize="sm" fontWeight="bold" color="brand.600" mb={1}>
          {new Date(label).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Text>
        <Flex align="baseline">
          <Text fontSize="lg" color="black" fontWeight="bold" mr={1}>
            {currency === 'USD'
              ? `$${payload[0].value?.toFixed(2)}`
              : `${payload[0].value?.toFixed(6)}`}
          </Text>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            {currency === 'USD'
              ? t('hardware-list.historical-chart.labels.usd-day')
              : t('hardware-list.historical-chart.labels.btc-day')}
          </Text>
        </Flex>
      </Box>
    )
  }

  return null
}

const MinerChart = ({
  showTitle = true,
  title = '',
  hardwareHashrate = 0,
  isSalePage = false,
  hideHashrate = false,
}) => {
  const t = useTranslations()
  const alpha700 = useColorModeValue('blackAlpha.700', 'whiteAlpha.700')
  const alpha800 = useColorModeValue('blackAlpha.800', 'whiteAlpha.800')
  const axisColor = useColorModeValue('#F0F0F0', '#222222')
  const lineColor = useColorModeValue('#9CCCE6', '#2B6CB0')
  const areaColor = useColorModeValue('#DDEDF5', '#0A5C84')
  const areaTransitionColor = useColorModeValue('#DDEDF500', '#0A5C8400')
  const selectedBg = useColorModeValue('white', 'whiteAlpha.300')
  const unselected = useColorModeValue('gray.600', 'whiteAlpha.700')
  const transitionBg = useColorModeValue('whiteAlpha.300', 'blackAlpha.300')
  const buttonGroupBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.200')
  const dropdownBg = useColorModeValue('white', 'primaryElevation')
  const dropdownBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200')
  const activeColor = useColorModeValue('black', 'white')
  const inactiveColor = useColorModeValue('black', 'whiteAlpha.700')
  const hashrateBg = useColorModeValue('white', 'whiteAlpha.200')
  const [filteredData, setFilteredData] = useState<
    { time: string; value: number }[]
  >([])
  const [currency, setCurrency] = useState<'USD' | 'BTC'>('USD')
  const [selectedRange, setSelectedRange] = useState('3 M')
  const [graphParameterLabels, setGraphParameterLabels] =
    useState<GraphParameterLabelsProps>({
      hashrate: {
        label: 'Hashrate',
        unit: 'TH/s',
        value: hardwareHashrate,
      },
    })

  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [inputHashrate, setInputHashrate] = useState<number | ''>(
    hardwareHashrate
  )

  const RANGE_OPTIONS = [
    {
      label: t('hardware-list.historical-chart.labels.1-day'),
      days: 3,
    },
    {
      label: t('hardware-list.historical-chart.labels.7-days'),
      days: 7,
    },
    {
      label: t('hardware-list.historical-chart.labels.30-days'),
      days: 30,
    },
    {
      label: t('hardware-list.historical-chart.labels.3-months'),
      days: 90,
    },
    {
      label: t('hardware-list.historical-chart.labels.6-months'),
      days: 180,
    },
    {
      label: t('hardware-list.historical-chart.labels.1-year'),
      days: 365,
    },
    {
      label: t('hardware-list.historical-chart.labels.3-years'),
      days: 1095,
    },
  ]

  const threeYearsFromNow = useMemo(() => {
    return subYears(new Date(), 3)
  }, [])

  const { data: hashpriceData } = useQuery<IHashpriceHistory>(
    getHashpriceHistory,
    {
      variables: {
        filter: {
          priceDate: {
            greaterThanOrEqualTo: threeYearsFromNow,
          },
        },
      },
    }
  )

  useEffect(() => {
    if (hashpriceData) {
      filterDataByRange(selectedRange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hashpriceData,
    selectedRange,
    currency,
    graphParameterLabels.hashrate.value,
  ])

  const filterDataByRange = (range: string) => {
    if (!hashpriceData?.getHashprice.nodes.length) return
    const hashpriceHistory = hashpriceData.getHashprice.nodes

    const days =
      Number(RANGE_OPTIONS.find((r) => r.label === range)?.days) || 90
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)

    const filtered = hashpriceHistory
      .filter((entry) => new Date(entry.priceDate) >= cutoffDate)
      .sort(
        (a, b) =>
          new Date(a.priceDate).getTime() - new Date(b.priceDate).getTime()
      )

    const transformedData = filtered.map((entry) => {
      const { profitBtc, profitUsd } = getMiningProfitByHashrate(
        entry.priceUsd,
        entry.priceBtc,
        graphParameterLabels.hashrate.value
      )
      return {
        time: entry.priceDate,
        value: currency === 'USD' ? profitUsd : profitBtc,
      }
    })

    setFilteredData(transformedData)
  }

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    if (value === '' || /^[0-9\b]+$/.test(value)) {
      const sanitizedValue = value.replace(/^0+/, '') || ''
      setInputHashrate(sanitizedValue === '' ? '' : Number(sanitizedValue))
    }
  }

  useEffect(() => {
    const boolsubmit = Object.keys(graphParameterLabels).some((key) => {
      return (
        graphParameterLabels[key as keyof GraphParameterLabelsProps].value === 0
      )
    }, {})
    setDisabledSubmit(boolsubmit)
  }, [graphParameterLabels])

  const setParametersByGraph = () => {
    setGraphParameterLabels({
      hashrate: {
        ...graphParameterLabels.hashrate,
        value: Number(inputHashrate),
      },
    })
  }

  const dateTickFormatter = (isoDate: string) => {
    const date = new Date(isoDate)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }
  const textUSD =
    currency === 'BTC'
      ? t('hardware-list.historical-chart.titleBTC')
      : t('hardware-list.historical-chart.title')

  if (!hashpriceData) {
    return null
  }

  return (
    <>
      <Flex
        justify={'space-between'}
        align={{ base: 'start', lg: 'center' }}
        flexDirection={{ base: 'column', lg: 'row' }}
      >
        <Text
          fontSize={{ base: '20px', md: '20px' }}
          fontWeight={{ base: '500', md: '700' }}
        >
          {showTitle ? textUSD : title}
        </Text>
        {hideHashrate && (
          <HStack
            spacing="8px"
            flexDirection={{ base: 'row', md: 'initial' }}
            alignItems="center"
            align="center"
            w="auto"
            mt={{ base: '20px', lg: 0 }}
          >
            <Text color={alpha700} fontSize={'xs'}>
              {graphParameterLabels.hashrate.label}
            </Text>
            <InputGroup
              w="auto"
              justifyContent="start"
              alignItems="center"
              borderRadius="6px"
              bg="blackAlpha.100"
              p="1px"
            >
              <Input
                type="number"
                name="hashrate"
                onChange={handleChangeValue}
                value={inputHashrate}
                fontSize="12px"
                maxW="46px"
                p="8px"
                borderWidth={0}
                borderStartRadius="4px!"
                bg={hashrateBg}
              />
              <InputRightAddon p="8px" borderWidth={0}>
                <Text fontSize="12px" color={alpha700}>
                  TH
                </Text>
              </InputRightAddon>
            </InputGroup>
            <Button
              px="12px"
              title="Submit"
              onClick={setParametersByGraph}
              variant="blue"
              disabled={disabledSubmit}
            >
              {t('buttons.submit')}
            </Button>
          </HStack>
        )}
      </Flex>
      <Divider
        display={isSalePage ? 'none' : 'initial'}
        color="bg-gray-300"
        mb="7"
      />
      <Flex justify="space-between" align="center" my={4}>
        <ButtonGroup
          bg={buttonGroupBg}
          p="2px"
          borderRadius="8px"
          spacing={0}
          display={{ base: 'none', md: 'initial' }}
        >
          {RANGE_OPTIONS.map((range) => (
            <Button
              key={range.label}
              borderRadius="8px"
              px="16px"
              py="8px"
              fontWeight="600"
              fontSize="14px"
              boxShadow={
                selectedRange === range.label
                  ? '0 2px 4px 0 rgba(0, 0, 0, 0.25)'
                  : 'none'
              }
              bg={selectedRange === range.label ? selectedBg : 'transparent'}
              color={selectedRange === range.label ? '' : unselected}
              _hover="none"
              _active={{ bg: transitionBg }}
              onClick={() => {
                setSelectedRange(range.label)
              }}
            >
              {range.label}
            </Button>
          ))}
        </ButtonGroup>
        <Box display={{ base: 'block', md: 'none' }}>
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon boxSize={4} />}
              bg={dropdownBg}
              borderRadius="6px"
              borderWidth={1}
              borderColor={dropdownBorderColor}
              fontSize="14px"
              lineHeight="20px"
              px="12px"
              py="10px"
              _active="none"
            >
              {selectedRange}
            </MenuButton>
            <MenuList
              bg={dropdownBg}
              borderColor={dropdownBorderColor}
              py="0px"
            >
              {RANGE_OPTIONS.map((range) => (
                <MenuItem
                  key={range.label}
                  onClick={() => setSelectedRange(range.label)}
                  fontSize="14px"
                  lineHeight="20px"
                  px="16px"
                  py="15px"
                  bg={
                    selectedRange === range.label ? dropdownBg : 'transparent'
                  }
                  color={
                    selectedRange === range.label ? activeColor : inactiveColor
                  }
                >
                  {range.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Box>
        <ButtonGroup bg={buttonGroupBg} p="2px" borderRadius="8px" spacing={0}>
          <Button
            onClick={() => setCurrency('BTC')}
            borderRadius="8px"
            px="16px"
            py="8px"
            fontWeight="400"
            boxShadow={
              currency === 'BTC' ? '0 2px 4px 0 rgba(0, 0, 0, 0.25)' : 'none'
            }
            bg={currency === 'BTC' ? selectedBg : 'transparent'}
            color={currency === 'BTC' ? '' : unselected}
            _hover="none"
            _active={{ bg: transitionBg }}
          >
            BTC
          </Button>
          <Button
            onClick={() => setCurrency('USD')}
            borderRadius="8px"
            px="16px"
            py="8px"
            fontWeight="400"
            boxShadow={
              currency === 'USD' ? '0 2px 4px 0 rgba(0, 0, 0, 0.25)' : 'none'
            }
            bg={currency === 'USD' ? selectedBg : 'transparent'}
            color={currency === 'USD' ? '' : unselected}
            _hover="none"
            _active={{ bg: transitionBg }}
          >
            USD
          </Button>
        </ButtonGroup>
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={2}>
        {currency === 'USD'
          ? t('hardware-list.historical-chart.labels.usd-day')
          : t('hardware-list.historical-chart.labels.btc-day')}
      </Text>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={filteredData}>
          <defs>
            <linearGradient id="revenueArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="50%" stopColor={areaColor} stopOpacity={0.9} />
              <stop
                offset="90%"
                stopColor={areaTransitionColor}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={axisColor} />
          <XAxis
            dataKey="time"
            tick={{ fill: '#A3A3A3' }}
            tickFormatter={dateTickFormatter}
            axisLine={{ stroke: axisColor }}
            tickLine={{ stroke: axisColor }}
            tickSize={0}
            tickMargin={10}
            fontSize={10}
          />
          <YAxis
            width={35}
            tick={{ fill: '#A3A3A3' }}
            axisLine={{ stroke: axisColor }}
            tickLine={{ stroke: axisColor }}
            tickSize={0}
            tickMargin={10}
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip currency={currency} />} />
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ paddingTop: 15 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            name="24h revenue"
            stroke={lineColor}
            strokeWidth={2}
            fill="url(#revenueArea)"
            dot={false}
            activeDot={{ r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>
      <Text
        mt="20px"
        fontSize={{ base: '12px', md: '16px' }}
        fontWeight="500"
        color={alpha800}
      >
        <Text as="span" color="red.500" mt="-0.5" mr="1">
          *
        </Text>
        {t('hardware-list.historical-chart.disclaimer-results')}
      </Text>
    </>
  )
}

export default MinerChart
